import React, { useContext, useEffect, useState, useMemo } from "react";
import { Button, Col, FormControl, InputGroup, Row, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../Context/globalContext";
import FirebaseApp from '../firebase';
import { ref, onValue, get } from 'firebase/database';
import moment from "moment/moment";
import { getReferredDevices } from "../Services/userServices";

export const Devices = () => {

    const globalContext = useContext(GlobalContext);
    const navigate = useNavigate();
    const { company_key } = useParams();
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    useEffect(() => {
        fetchAllRelatedDevices();
    }, [])

    const normalizeReferralDeviceId = (device) => {
        if (!device) {
            return null;
        }
        if (typeof device === 'string') {
            return device;
        }
        if (typeof device === 'object') {
            return device.device_id || device.deviceId || device.referred_device_id || device.referredDeviceId || null;
        }
        return null;
    };

    const fetchSubscriptionStatusForDevices = async (deviceIds, dbInstance) => {
        if (!deviceIds.length) {
            return {};
        }
        const result = {};
        let db = dbInstance;
        try {
            if (!db) {
                db = await FirebaseApp();
            }
            const promises = deviceIds.map(async (deviceId) => {
                try {
                    const paymentRef = ref(db, `MindResetDb/PaymentInformation/${deviceId}/anxiety`);
                    const snapshot = await get(paymentRef);
                    let hasMonthly = false;
                    let hasYearly = false;
                    if (snapshot.exists()) {
                        const paymentData = snapshot.val();
                        if (paymentData?.paymentType === 'PROGRAM_PURCHASED') {
                            if (paymentData?.subscriptionDuration === 'MONTHLY') {
                                hasMonthly = true;
                            } else if (paymentData?.subscriptionDuration === 'YEARLY') {
                                hasYearly = true;
                            }
                        }
                    }
                    result[deviceId] = { hasMonthly, hasYearly };
                } catch (err) {
                    console.log('fetchSubscriptionStatusForDevices device error:', deviceId, err);
                    result[deviceId] = { hasMonthly: false, hasYearly: false };
                }
            });
            await Promise.all(promises);
        } catch (err) {
            console.log('fetchSubscriptionStatusForDevices error:', err);
        }
        return result;
    };

    const updateReferralCounts = async (records, dbInstance) => {
        const deviceIds = records.map(d => d.device_id).join(',');
        if (!deviceIds) {
            setData(records);
            return;
        }
        try {
            const res = await getReferredDevices(deviceIds);
            const referralData = res.data.referred_devices || {};
            const allReferredDeviceIds = new Set();

            Object.values(referralData).forEach((list) => {
                (list || []).forEach((device) => {
                    const deviceId = normalizeReferralDeviceId(device);
                    if (deviceId) {
                        allReferredDeviceIds.add(deviceId);
                    }
                });
            });

            const subscriptionStatus = await fetchSubscriptionStatusForDevices(Array.from(allReferredDeviceIds), dbInstance);

            const updatedData = records.map(d => {
                const referredDeviceList = referralData[d.device_id] || [];
                const counts = referredDeviceList.reduce((acc, device) => {
                    const deviceId = normalizeReferralDeviceId(device);
                    if (!deviceId) {
                        return acc;
                    }
                    const currentStatus = subscriptionStatus[deviceId];
                    if (currentStatus?.hasMonthly) {
                        acc.monthly += 1;
                    }
                    if (currentStatus?.hasYearly) {
                        acc.yearly += 1;
                    }
                    return acc;
                }, { monthly: 0, yearly: 0 });

                return {
                    ...d,
                    referred_devices: referredDeviceList,
                    referred_count: referredDeviceList.length,
                    monthly_subscription_count: counts.monthly,
                    yearly_subscription_count: counts.yearly
                };
            });
            setData(updatedData);
        } catch (err) {
            console.log('updateReferralCounts error:', err);
            setData(records);
        }
    }

    const fetchAllRelatedDevices = () => {
        globalContext.setLoader(true);
        FirebaseApp().then(db => {
            const dbRef = ref(db, `UsedCompanyKeys/${company_key}`);
            return onValue(dbRef, (snapshot) => {
                const records = []
                snapshot.forEach(childSnapshot => {
                    let user_id = childSnapshot.val().company_user_identifier || null;
                    let joined_at = childSnapshot.val().joined_at || null;
                    let session_count = childSnapshot.val().timestamp ? Object.keys(childSnapshot.val().timestamp).length : 0;
                    let status = childSnapshot.val()?.status ? 'Active' : 'Inactive';
                    records.push({ "device_id": childSnapshot.key, "session_count": session_count, "joined_at": joined_at, "user_id": user_id, "status": status, "referred_devices": [], "referred_count": 0, "monthly_subscription_count": 0, "yearly_subscription_count": 0 });
                })
                updateReferralCounts(records, db)
                    .finally(() => {
                        globalContext.setLoader(false);
                    });
            }, (err) => {
                console.log('error --->', err);
                globalContext.setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            globalContext.setLoader(false);
        })
    }

    const filteredData = useMemo(() => {
        const searchText = search.toLowerCase();
        return data
            .filter(d => showInactive ? true : d.status === 'Active')
            .filter(d =>
                d.device_id.toLowerCase().includes(searchText)
            );
    }, [search, data, showInactive]);

    const getDevicesSessions = async () => {
        const sessions = [];
        try {
            const db = await FirebaseApp();
            const dbRef = ref(db, `UsedCompanyKeys/${company_key}`);

            const snapshot = await get(dbRef);
            if (snapshot.exists()) {
                const promises = [];

                snapshot.forEach(childSnapshot => {
                    const device_id = childSnapshot.key;
                    const user_id = childSnapshot.val().company_user_identifier || "-";

                    const timestampRef = ref(db, `UsedCompanyKeys/${company_key}/${device_id}/timestamp`);
                    const p = get(timestampRef).then(tsSnap => {
                        if (tsSnap.exists()) {
                            tsSnap.forEach(sessionSnap => {
                                sessions.push({
                                    user_id,
                                    device_id,
                                    session: JSON.stringify(sessionSnap.val()),
                                    timestamp: sessionSnap.key
                                });
                            });
                        }
                    });

                    promises.push(p);
                });

                await Promise.all(promises);
            }
        } catch (err) {
            console.log('getDevicesSessions error:', err);
        }

        return sessions;
    };

    const exportToCSV = async () => {
        try {
            globalContext.setLoader(true);
            const data = await getDevicesSessions();

            const csvHeader = 'UserIdentifier,DeviceID,Session,Timestamp\n';
            const csvData = data.map(d =>
                `${d.user_id},${d.device_id},${d.session},${moment(Number(d.timestamp)).format('DD-MM-YYYY hh:mm A')}`
            ).join('\n');

            const csvContent = csvHeader + csvData;
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${company_key}_all_sessions.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log('exportToCSV error:', err);
        } finally {
            globalContext.setLoader(false);
        }
    };

    const listTitle = showInactive ? 'All Devices' : 'Active Devices';

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <div className="back-button-container">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                </div>
                <div className="session-header">
                    <h1><span className="text-orange">{company_key}</span> {listTitle}</h1>
                    <Button className="export-button" onClick={exportToCSV}>
                        Export to CSV
                    </Button>
                </div>
                <Row className="d-flex justify-content-between">
                    <Col md={4}>
                        <div className="text-left mt-3">
                            <InputGroup>
                                <FormControl className="search-field" placeholder="Search by Device ID" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
                            </InputGroup>
                        </div>
                    </Col>
                    <Col md="auto">
                        <div className="d-flex align-items-center mt-3">
                            <Button variant="secondary" onClick={() => setShowInactive(v => !v)}>
                                {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
            <section className="section dashboard">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>User Indentifier</th>
                            <th>Device ID</th>
                            <th>Referral Count</th>
                            <th>Referral Monthly Subscribers</th>
                            <th>Referral Yearly Subscribers</th>
                            <th>Total Session Counts</th>
                            <th>Status</th>
                            <th>Joined At</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length > 0 ?
                            filteredData.map((d) =>
                                <tr key={d.device_id}>
                                    <td>{d.user_id || '-'}</td>
                                    <td>{d.device_id}</td>
                                    <td>{d.referred_count || 0}</td>
                                    <td>{d.monthly_subscription_count || 0}</td>
                                    <td>{d.yearly_subscription_count || 0}</td>
                                    <td>{d.session_count}</td>
                                    <td>{d.status}</td>
                                    <td>{d.joined_at ? moment(d.joined_at).format('DD-MM-YYYY hh:mm A') : '-'}</td>
                                    <td>
                                        <Button className="btn btn-primary" onClick={() => navigate(`/devices/${company_key}/details/${d.device_id}`)}>View</Button>
                                    </td>
                                </tr>
                            ) :
                            <tr>
                                <td colSpan={9}>
                                    <h5 className="text-center">No data</h5>
                                </td>
                            </tr>

                        }
                    </tbody>
                </Table>
            </section>
        </main >
    )

}
